import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { BookingProvider } from './context/BookingContext';
import Navbar from './components/Navbar';
import SaaSFooter from './components/SaaSFooter';
import FloatingContactActions from './components/FloatingContactActions';
import ScrollToTop from './components/ScrollToTop';
import { areaSeoRoutePaths, tirunelveliSeoRoutes } from './pages/SeoPages/generatedSeoRoutes';
import { navakailayamSeoPages } from './pages/SeoPages/NavakailayamSEO/navakailayamSeoPages';
import { seoKeywords as navakailayamSeoKeywords } from './pages/SeoPages/NavakailayamSEO/seoKeywords';

const HomePage = lazy(() => import('./pages/HomePage'));
const BookingPage = lazy(() => import('./pages/Booking'));
const MyRidesPage = lazy(() => import('./pages/MyRides'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const FAQ = lazy(() => import('./pages/FAQ'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostDetail = lazy(() => import('./pages/BlogPostDetail'));
const VehicleSelection = lazy(() => import('./components/VehicleSelection'));
const ContactDetailsPage = lazy(() => import('./components/ContactDetailsPage'));
const TourBookingPage = lazy(() => import('./pages/TourBookingPage'));
const VehiclePage = lazy(() => import('./pages/VehiclePage'));
const AreaCabService = lazy(() => import('./pages/SeoPages/AreaCabService'));
const CabsInTirunelveli = lazy(() => import('./pages/SeoPages/cabsintirunelveli'));
const CallTaxiTirunelveli = lazy(() => import('./pages/SeoPages/calltaxitirunelveli'));
const NavaTirupathiCab = lazy(() => import('./pages/SeoPages/navatirupathicab'));
const TempoTravellerTirunelveli = lazy(() => import('./pages/SeoPages/tempotravellertirunelveli'));
const TirunelveliSeoRoute = lazy(() => import('./pages/SeoPages/TirunelveliSeoRoute'));
const SeoPagesHub = lazy(() => import('./pages/SeoPages/SeoPagesHub'));
const TirunelveliTaxiLanding = lazy(() => import('./pages/SeoPages/25july2025Seo/Tirunelvelitaxilanding'));
const TirunelveliCabSEO = lazy(() => import('./pages/SeoPages/Sep4/TirunelveliCabSEO'));
const KeywordPage = lazy(() => import('./pages/SeoPages/Sep4/TirunelveliCabSEO').then(m => ({ default: m.KeywordPage })));
const NavaKailayamTaxi = lazy(() => import('./pages/SeoPages/NavakailayamSEO/Navakailayamtaxi'));
const NavaKailayamCab = lazy(() => import('./pages/SeoPages/NavakailayamSEO/Navakailayamcab'));
const BookCabNavaKailayam = lazy(() => import('./pages/SeoPages/NavakailayamSEO/Bookcabnavakailayam'));
const TirunelveliToNavaKailayamCab = lazy(() => import('./pages/SeoPages/NavakailayamSEO/Tirunelvelitonavakailayamcab'));
const TirunelveliToNavaKailayamTaxi = lazy(() => import('./pages/SeoPages/NavakailayamSEO/Tirunelvelitonavakailayamtaxi'));
const TirunelveliCabServicesSeo = lazy(() => import('./pages/SeoPages/NavakailayamSEO/tirunelveliCabServices'));
const TirunelveliRailwayStationCabs = lazy(() => import('./pages/SeoPages/NavakailayamSEO/TirunelvelirailwayStationCabs'));
const TirunelveliHotelCabs = lazy(() => import('./pages/SeoPages/NavakailayamSEO/tirunelvelihotelCabs'));
const GeneratedKeywordSeoPage = lazy(() => import('./pages/SeoPages/13oct2026seopages/Seo'));

const navakailayamRouteComponents = {
  navaKailayamTaxi: NavaKailayamTaxi,
  navaKailayamCab: NavaKailayamCab,
  bookCabNavaKailayam: BookCabNavaKailayam,
  tirunelveliToNavaKailayamCab: TirunelveliToNavaKailayamCab,
  tirunelveliToNavaKailayamTaxi: TirunelveliToNavaKailayamTaxi,
  tirunelveliCabServices: TirunelveliCabServicesSeo,
  railwayStationCabs: TirunelveliRailwayStationCabs,
  hotelCabs: TirunelveliHotelCabs,
};

const routeSeo = {
  '/': {
    title: 'Aadvika Cabs - Best Cab Service in Tirunelveli | Taxi Booking Tirunelveli',
    description: 'Aadvika Cabs offers 24/7 reliable cab booking services in Tirunelveli for local rides, outstation trips, airport transfers, and temple tours.',
  },
  '/vehicle-selection': {
    title: 'Select Cab Type in Tirunelveli | Aadvika Cabs',
    description: 'Compare and choose hatchback, sedan, SUV, tempo traveller, and premium cabs for your Tirunelveli trip with Aadvika Cabs.',
  },
  '/contact-details': {
    title: 'Cab Booking Contact Details | Aadvika Cabs Tirunelveli',
    description: 'Share your contact details to confirm a Tirunelveli cab booking with Aadvika Cabs.',
  },
  '/booking': {
    title: 'Book Cab in Tirunelveli Online | Aadvika Cabs',
    description: 'Book local taxi, outstation cab, airport transfer, and temple tour rides online with Aadvika Cabs Tirunelveli.',
  },
  '/book-tour': {
    title: 'Book Tirunelveli Tour Taxi | Aadvika Cabs',
    description: 'Plan temple tours, family trips, and custom travel packages from Tirunelveli with Aadvika Cabs.',
  },
  '/vehicle': {
    title: 'Aadvika Cabs Vehicle Fleet | Tirunelveli Taxi Cars',
    description: 'View Aadvika Cabs fleet options for local and outstation taxi booking in Tirunelveli.',
  },
  '/rides': {
    title: 'My Rides | Aadvika Cabs',
    description: 'View ride booking details and trip history for Aadvika Cabs.',
  },
  '/profile': {
    title: 'Profile | Aadvika Cabs',
    description: 'Manage your Aadvika Cabs account profile and booking details.',
  },
  '/contactus': {
    title: 'Contact Aadvika Cabs Tirunelveli | 24/7 Taxi Booking',
    description: 'Contact Aadvika Cabs for 24/7 taxi booking, airport cab, outstation taxi, and tour cab service in Tirunelveli.',
  },
  '/login': {
    title: 'Login | Aadvika Cabs',
    description: 'Login to your Aadvika Cabs account for faster Tirunelveli cab booking.',
  },
  '/signup': {
    title: 'Sign Up | Aadvika Cabs',
    description: 'Create an Aadvika Cabs account for quick Tirunelveli taxi booking and ride management.',
  },
  '/services': {
    title: 'Cab Services in Tirunelveli | Aadvika Cabs',
    description: 'Explore local taxi, outstation cab, airport transfer, tempo traveller, and tour cab services from Aadvika Cabs Tirunelveli.',
  },
  '/faq': {
    title: 'Taxi Booking FAQ | Aadvika Cabs Tirunelveli',
    description: 'Find answers about Aadvika Cabs booking, pricing, airport transfers, outstation trips, and Tirunelveli taxi service.',
  },
  '/blogs': {
    title: 'Travel & Cab Booking Blogs | Aadvika Cabs Tirunelveli',
    description: 'Read the latest Tirunelveli travel guides, temple tour circuits, taxi fare tips, and cab booking insights from Aadvika Cabs.',
  },
  '/blog': {
    title: 'Travel & Cab Booking Blogs | Aadvika Cabs Tirunelveli',
    description: 'Read the latest Tirunelveli travel guides, temple tour circuits, taxi fare tips, and cab booking insights from Aadvika Cabs.',
  },
  '/blogs/tirunelveli-cab-services-guide-2026': {
    title: 'Comprehensive Tirunelveli Cab Services & Taxi Booking Directory (2026)',
    description: 'Complete guide to booking reliable local cabs, outstation taxis, airport transfers, and temple circuit cabs in Tirunelveli. Explore over 1000 verified cab service routes and keywords.',
  },
  '/seo-pages': {
    title: 'All Tirunelveli Cab Service Pages | Aadvika Cabs',
    description: 'Browse all Aadvika Cabs Tirunelveli area, route, and service landing pages from one index.',
  },
  '/taxi-service-in-tirunelveli': {
    title: 'Taxi Service in Tirunelveli | Aadvika Cabs Railway & Hotel Pickup',
    description: 'Book taxi service in Tirunelveli for railway station pickup, hotel pickup, local trips, outstation travel, tempo traveller, and verified 24/7 cab service.',
  },
  '/tirunelveli-cab-service': {
    title: 'Tirunelveli Cab Service - 1000+ Verified Booking Keywords | Aadvika Cabs',
    description: 'Explore 1000+ Tirunelveli cab service booking options, taxi fares, airport pickups, and local car rentals. Book 24/7 AC cabs with Aadvika Cabs.',
  },
  ...Object.fromEntries(
    navakailayamSeoPages.map((page) => [
      page.path,
      {
        title: navakailayamSeoKeywords[page.keywordId].metaTitle,
        description: navakailayamSeoKeywords[page.keywordId].metaDescription,
      },
    ])
  ),
};

const formatSlug = (slug = '') =>
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getGeneratedSeo = (pathname) => {
  if (pathname.startsWith('/tirunelveli-cab-keyword/')) {
    const keyword = pathname.replace('/tirunelveli-cab-keyword/', '');
    const formatted = keyword.split('-').join(' ');
    return {
      title: `${formatted} | Tirunelveli Cab Service`,
      description: `Get the best ${formatted} in Tirunelveli. Book now for affordable rates, 24/7 service, AC cabs, and reliable drivers. Call +91-8610341454.`
    };
  }

  if (pathname.startsWith('/blogs/') || pathname.startsWith('/blog/')) {
    const slug = pathname.replace(/^\/(blogs|blog)\//, '');
    const heading = formatSlug(slug);
    return {
      title: `${heading} | Aadvika Cabs Travel Blog`,
      description: `${heading} - Detailed travel advice, taxi tips, routes, and cab booking recommendations from Aadvika Cabs Tirunelveli.`
    };
  }

  if (!tirunelveliSeoRoutes.includes(pathname) && !areaSeoRoutePaths.includes(pathname)) {
    return null;
  }

  const cleanedPath = pathname.replace(/^\//, '');

  if (cleanedPath.startsWith('cabs-tirunelveli-to-')) {
    const destSlug = cleanedPath.replace(/^cabs-tirunelveli-to-/, '');
    const destination = formatSlug(destSlug);
    return {
      title: `Tirunelveli to ${destination} Cab Service | Outstation Taxi - Aadvika Cabs`,
      description: `Book outstation cab from Tirunelveli to ${destination} with Aadvika Cabs. Clean AC Sedan, Innova Crysta & Tempo Traveller at transparent per-km fares. 24/7 service.`,
    };
  }

  const areaName = formatSlug(
    cleanedPath
      .replace(/^cabs-in-/, '')
      .replace(/^call-taxi-in-/, '')
      .replace(/^taxi-in-/, '')
      .replace(/^car-rental-in-/, '')
      .replace(/^tempo-traveller-in-/, '')
      .replace(/^cabs-/, '')
  );

  const serviceName = cleanedPath.startsWith('call-taxi-in-')
    ? 'Call Taxi'
    : cleanedPath.startsWith('taxi-in-')
      ? 'Taxi'
      : cleanedPath.startsWith('car-rental-in-')
        ? 'Car Rental'
        : cleanedPath.startsWith('tempo-traveller-in-')
          ? 'Tempo Traveller'
          : 'Cab Service';

  return {
    title: `${serviceName} in ${areaName} | Aadvika Cabs Tirunelveli`,
    description: `Book ${serviceName.toLowerCase()} in ${areaName} with Aadvika Cabs. 24/7 cab booking, airport transfers, outstation trips, Tempo Traveller, verified drivers, and GST invoice support.`,
  };
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const privateRoutes = ['/login', '/signup', '/profile', '/rides', '/vehicle-selection', '/contact-details'];

const getCanonicalPath = (pathname) => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return `/${pathname.replace(/^\/+|\/+$/g, '')}`;
};

const normalizePathname = (pathname) => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return `/${pathname.replace(/^\/+|\/+$/g, '')}`;
};

const RouteSeo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPathname = normalizePathname(pathname);
    
    // Handle private/utility pages with noindex
    if (privateRoutes.includes(normalizedPathname)) {
      document.title = `${normalizedPathname.replace('/', '').toUpperCase()} | Aadvika Cabs`;
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
      return;
    }

    const seo = routeSeo[normalizedPathname] || getGeneratedSeo(normalizedPathname);
    if (!seo) {
      return;
    }

    const canonicalPath = getCanonicalPath(normalizedPathname);
    const canonicalUrl = canonicalPath === '/' 
      ? 'https://www.aadvikacabs.com/' 
      : `https://www.aadvikacabs.com${canonicalPath}`;

    document.title = seo.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="twitter:title"]', { property: 'twitter:title', content: seo.title });
    upsertMeta('meta[property="twitter:description"]', { property: 'twitter:description', content: seo.description });
    upsertMeta('meta[property="twitter:url"]', { property: 'twitter:url', content: canonicalUrl });
    upsertCanonical(canonicalUrl);
  }, [pathname]);

  return null;
};

const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center text-sm font-semibold text-gray-500">
    Loading...
  </div>
);

const App = () => {
  return (
    <BookingProvider>
      <Router>
        <ScrollToTop />
        <RouteSeo />
        <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
          <Navbar />
          
          <div className="pt-16 flex-grow">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/vehicle-selection" element={<VehicleSelection />} />
              <Route path="/contact-details" element={<ContactDetailsPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/book-tour" element={<TourBookingPage />} />
              <Route path="/vehicle" element={<VehiclePage />} />
              <Route path="/rides" element={<MyRidesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/contactus" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/services" element={<ServicePage />} />
              <Route path="/faq" element={<FAQ />} />
              
              {/* Blog Routes */}
              <Route path="/blogs" element={<BlogPage />} />
              <Route path="/blogs/:slug" element={<BlogPostDetail />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostDetail />} />

              {/* SEO Pages & 1000 Keywords Hub */}
              <Route path="/seo-pages" element={<SeoPagesHub />} />
              <Route path="/cab/:slug" element={<GeneratedKeywordSeoPage />} />
              <Route path="/tirunelveli-cab-service" element={<TirunelveliCabSEO />} />
              <Route path="/tirunelveli-cab-keyword/:keyword" element={<KeywordPage />} />
              <Route path="/taxi-service-in-tirunelveli" element={<TirunelveliTaxiLanding />} />

              {navakailayamSeoPages.map((page) => {
                const PageComponent = navakailayamRouteComponents[page.keywordId];
                return (
                  <Route
                    key={page.path}
                    path={page.path}
                    element={<PageComponent />}
                  />
                );
              })}
              <Route path="/cabs-in-tirunelveli" element={<CabsInTirunelveli />} />
              <Route path="/tirunelveli-cabs" element={<CabsInTirunelveli />} />
              <Route path="/car-rental-in-tirunelveli" element={<CabsInTirunelveli />} />
              <Route path="/cab-service-in-tirunelveli" element={<CabsInTirunelveli />} />
              <Route path="/call-taxi-in-tirunelveli" element={<CallTaxiTirunelveli />} />
              <Route path="/call-taxi-tirunelveli" element={<CallTaxiTirunelveli />} />
              <Route path="/nava-tirupathi-in-tirunelveli" element={<NavaTirupathiCab />} />
              <Route path="/nava-tirupathi-cab-tirunelveli" element={<NavaTirupathiCab />} />
              <Route path="/tempo-traveller-in-tirunelveli" element={<TempoTravellerTirunelveli />} />
              <Route path="/tempo-traveller-tirunelveli" element={<TempoTravellerTirunelveli />} />
              {areaSeoRoutePaths.map((path) => (
                <Route key={path} path={path} element={<AreaCabService />} />
              ))}
              {tirunelveliSeoRoutes.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={<TirunelveliSeoRoute />}
                />
              ))}
              {/* 404 fallback */}
              <Route path="*" element={
                <div className="flex flex-col items-center justify-center min-h-[60vh] py-16">
                  <h1 className="text-6xl font-black text-cyan-600 mb-4">404</h1>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h2>
                  <p className="text-gray-600 mb-6">The page you are looking for does not exist or has moved.</p>
                  <a href="/" className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-6 py-2.5 rounded-xl transition">
                    Return to Home
                  </a>
                </div>
              } />
            </Routes>
            </Suspense>
          </div>
          <SaaSFooter />
          <FloatingContactActions />
        </div>
      </Router>
    </BookingProvider>
  );
};

export default App;
